import { Col, DatePicker, Divider, Drawer, Form, Row, Select } from 'antd';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import BoxContainer from '@src/components/BoxContainer';
import Title from '@src/components/BoxContainer/components/Title';
import FilterContainer from '@src/components/FilterContainer';
import type { FilterContainerActionButton } from '@src/components/FilterContainer/FilterContainer.interfaces';
import { SHIFT_CHANGES_REQUEST_BY } from '@src/containers/App/TimeTracker/constants';
import {
  ABSENCE_REASONS,
  ABSENCE_TYPES,
} from '@src/containers/App/TimeTracker/Main/components/reports/shared/constants';
import type { CreateShiftChangesParams } from '@src/models/interfaces/attendanceEvent.interface';
import { DateUtil } from '@src/packages/utils/dates';
import IntlMessages from '@src/util/IntlMessages';

import type { MatrixAttendanceDrawerData } from '../../hooks/useWorkJourneyMatrixAttendanceDrawer';
import {
  MATRIX_ATTENDANCE_CORRECTION_TYPE,
  type MatrixAttendanceCorrectionType,
} from './constants';
import { useMatrixEditAttendanceDrawer } from './hooks/useMatrixEditAttendanceDrawer';
import styles from './styles.module.less';

const { Option } = Select;

/** Reuse Time Tracker edit-mark copy for field labels (UI only; component is matrix-local). */
const TT_PREFIX = 'timeTracker.reports.editMark';

type MatrixEditAttendanceDrawerProps = {
  open: boolean;
  data: MatrixAttendanceDrawerData | null;
  organizationId?: string | null;
  onClose: () => void;
  onSuccess?: (changes: CreateShiftChangesParams) => void;
};

const formatTs = (value?: { _seconds?: number } | null): string => {
  const ms = DateUtil.getTimestampMs(value ?? null);
  if (ms == null) return '—';
  return DateUtil.toDateString(
    ms,
    `${DateUtil.DEFAULT_DT_DATE_FORMAT} ${DateUtil.DEFAULT_TIME_FORMAT}`,
  );
};

export const MatrixEditAttendanceDrawer = ({
  open,
  data,
  organizationId,
  onClose,
  onSuccess,
}: MatrixEditAttendanceDrawerProps) => {
  const intl = useIntl();
  const {
    form,
    saving,
    correctionReason,
    handleCorrectionReasonChange,
    handleSubmit,
    handleClose,
  } = useMatrixEditAttendanceDrawer({
    open,
    data,
    organizationId,
    onClose,
    onSuccess,
  });

  const actionButtons = useMemo((): FilterContainerActionButton[] => {
    return [
      {
        label: <IntlMessages id="button.save" />,
        action: handleSubmit,
        disabled: saving || !data?.record,
        type: 'primary',
        loading: saving,
      },
    ];
  }, [data?.record, handleSubmit, saving]);

  const datePickerFormat = `${DateUtil.DEFAULT_DT_DATE_FORMAT} ${DateUtil.DEFAULT_TIME_FORMAT}`;

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      width={560}
      title={null}
      closable={false}
      footer={null}
      destroyOnClose
      className={styles.drawer}
    >
      <BoxContainer>
        <BoxContainer fullContent fixed bottomBorder>
          <FilterContainer
            title={
              <Title.Header
                value={intl.formatMessage({
                  id: 'workJourney.management.matrix.attendance.edit.title',
                })}
              />
            }
            goBack={handleClose}
            actionButtons={actionButtons}
          />
        </BoxContainer>

        <BoxContainer fullContent>
          {data && (
            <div className={styles.summary}>
              <div className={styles.summaryTitle}>
                <IntlMessages id={`${TT_PREFIX}.selectedMarking.title`} />
              </div>
              <div className={styles.summaryRow}>
                <span className={styles.summaryLabel}>
                  <IntlMessages id={`${TT_PREFIX}.userInfo.name`} />
                </span>
                <span>{data.user.displayName}</span>
              </div>
              <div className={styles.summaryRow}>
                <span className={styles.summaryLabel}>
                  <IntlMessages id={`${TT_PREFIX}.userInfo.startMarking`} />
                </span>
                <span>{formatTs(data.record.actualStartAt ?? data.record.scheduledStartAt)}</span>
              </div>
              <div className={styles.summaryRow}>
                <span className={styles.summaryLabel}>
                  <IntlMessages id={`${TT_PREFIX}.userInfo.endMarking`} />
                </span>
                <span>{formatTs(data.record.actualEndAt ?? data.record.scheduledEndAt)}</span>
              </div>
            </div>
          )}

          <Form form={form} layout="vertical" className={styles.form}>
            <Divider orientation="left">
              <Title.SubTitle value={intl.formatMessage({ id: `${TT_PREFIX}.correction.title` })} />
            </Divider>

            <Form.Item
              name="requestedBy"
              label={intl.formatMessage({ id: `${TT_PREFIX}.correction.requestedBy.label` })}
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({
                    id: `${TT_PREFIX}.correction.requestedBy.required`,
                  }),
                },
              ]}
              initialValue={SHIFT_CHANGES_REQUEST_BY.SUPERVISOR}
            >
              <Select disabled={saving}>
                <Option value={SHIFT_CHANGES_REQUEST_BY.SUPERVISOR}>
                  {intl.formatMessage({ id: `${TT_PREFIX}.correction.requestedBy.employer` })}
                </Option>
                <Option value={SHIFT_CHANGES_REQUEST_BY.EMPLOYEE}>
                  {intl.formatMessage({ id: `${TT_PREFIX}.correction.requestedBy.employee` })}
                </Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="correctionReason"
              label={intl.formatMessage({ id: `${TT_PREFIX}.correction.reason.label` })}
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({ id: `${TT_PREFIX}.correction.reason.required` }),
                },
              ]}
            >
              <Select
                disabled={saving}
                onChange={(value: MatrixAttendanceCorrectionType) =>
                  handleCorrectionReasonChange(value)
                }
              >
                <Option value={MATRIX_ATTENDANCE_CORRECTION_TYPE.TIME_ADJUSTMENT}>
                  {intl.formatMessage({
                    id: `${TT_PREFIX}.correction.reason.markingCorrection`,
                  })}
                </Option>
                <Option value={MATRIX_ATTENDANCE_CORRECTION_TYPE.LEAVE}>
                  {intl.formatMessage({ id: `${TT_PREFIX}.correction.reason.absence` })}
                </Option>
              </Select>
            </Form.Item>

            {correctionReason === MATRIX_ATTENDANCE_CORRECTION_TYPE.TIME_ADJUSTMENT && (
              <>
                <Divider orientation="left">
                  <Title.SubTitle
                    value={intl.formatMessage({
                      id: `${TT_PREFIX}.correction.workdayModification.title`,
                    })}
                  />
                </Divider>
                <Row gutter={12}>
                  <Col span={24}>
                    <Form.Item
                      name="newStartMarking"
                      label={intl.formatMessage({
                        id: `${TT_PREFIX}.correction.newStartMarking.label`,
                      })}
                      rules={[
                        {
                          required: true,
                          message: intl.formatMessage({
                            id: `${TT_PREFIX}.correction.newStartMarking.required`,
                          }),
                        },
                      ]}
                    >
                      <DatePicker
                        showTime
                        disabled={saving}
                        className="gx-w-100"
                        format={datePickerFormat}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      name="newEndMarking"
                      label={intl.formatMessage({
                        id: `${TT_PREFIX}.correction.newEndMarking.label`,
                      })}
                      rules={[
                        {
                          required: true,
                          message: intl.formatMessage({
                            id: `${TT_PREFIX}.correction.newEndMarking.required`,
                          }),
                        },
                      ]}
                    >
                      <DatePicker
                        showTime
                        disabled={saving}
                        className="gx-w-100"
                        format={datePickerFormat}
                      />
                    </Form.Item>
                    <Form.Item
                      noStyle
                      shouldUpdate={(prev, next) =>
                        prev.newStartMarking !== next.newStartMarking ||
                        prev.newEndMarking !== next.newEndMarking
                      }
                    >
                      {() => {
                        const start = form.getFieldValue('newStartMarking');
                        const end = form.getFieldValue('newEndMarking');
                        const hours = DateUtil.getTimeDifference(start, end, 'hours');
                        if (typeof hours !== 'number' || hours <= 10) return null;
                        return (
                          <div className={styles.hoursWarning}>
                            {intl.formatMessage({
                              id: `${TT_PREFIX}.correction.workdayModification.maxDailyHours.warning`,
                            })}
                          </div>
                        );
                      }}
                    </Form.Item>
                  </Col>
                </Row>

                <Divider orientation="left">
                  <Title.SubTitle
                    value={intl.formatMessage({
                      id: `${TT_PREFIX}.correction.breakModification.title`,
                    })}
                  />
                </Divider>
                <Form.Item
                  name="newBreakStartMarking"
                  label={intl.formatMessage({
                    id: `${TT_PREFIX}.correction.newBreakStartMarking.label`,
                  })}
                >
                  <DatePicker
                    showTime
                    disabled={saving}
                    className="gx-w-100"
                    format={datePickerFormat}
                  />
                </Form.Item>
                <Form.Item
                  name="newBreakEndMarking"
                  label={intl.formatMessage({
                    id: `${TT_PREFIX}.correction.newBreakEndMarking.label`,
                  })}
                >
                  <DatePicker
                    showTime
                    disabled={saving}
                    className="gx-w-100"
                    format={datePickerFormat}
                  />
                </Form.Item>
              </>
            )}

            {correctionReason === MATRIX_ATTENDANCE_CORRECTION_TYPE.LEAVE && (
              <>
                <Form.Item
                  name="leaveExcuse"
                  label={intl.formatMessage({ id: `${TT_PREFIX}.correction.isExcused.label` })}
                  rules={[
                    {
                      required: true,
                      message: intl.formatMessage({
                        id: `${TT_PREFIX}.correction.isExcused.required`,
                      }),
                    },
                  ]}
                >
                  <Select disabled={saving}>
                    {ABSENCE_REASONS.map(option => (
                      <Option key={option.code} value={option.code}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item
                  name="absenceReason"
                  label={intl.formatMessage({
                    id: `${TT_PREFIX}.correction.absenceReason.label`,
                  })}
                >
                  <Select disabled={saving} showSearch optionFilterProp="children" allowClear>
                    {ABSENCE_TYPES.map(option => (
                      <Option key={option.code} value={option.code}>
                        {option.label} ({option.short})
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </>
            )}
          </Form>
        </BoxContainer>
      </BoxContainer>
    </Drawer>
  );
};

export default MatrixEditAttendanceDrawer;
